// modules
import { get } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import {
  setEditableTask,
  setEditableTaskComments,
  setHistoryItems,
  setPatientListTaskModal
} from 'src/store/modules/tasks'

// constants
import {
  DELETE_ATTACHMENT,
  DELETE_TASK_ATTACHMENT,
  DELETE_TASK_COMMENT,
  GET_DELETE_ATTACHMENT,
  GET_DELETE_TASK,
  GET_DELETE_TASK_ATTACHMENT,
  GET_TASK,
  GET_TASK_COMMENTS,
  GET_TASK_COMMENT_ATTACHMENT,
  GET_TASK_HISTORY,
  PATIENTS,
  POST_ATTACHMENT,
  POST_TASK,
  POST_TASK_COMMENT,
  TASK_COMMENT_DELETE_MESSAGE,
  TASK_COMMENT_SAVE_MESSAGE,
  TASK_DELETE_MESSAGE,
  TASK_SAVE_MESSAGE
} from 'src/utils/constants'
import { showSuccessModal } from 'src/utils/helpers/dialogs'

export const fetchTask =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { params = {} } = config

    try {
      const item = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_DELETE_TASK,
          params
        }
      })

      dispatch(setEditableTask(item))

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const saveTask =
  (config: any = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_TASK,
          params
        },
        payload
      })

      showSuccessModal({
        content: TASK_SAVE_MESSAGE,
        centered: true
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const saveAttachment = async (config: any = {}) => {
  const { data = {} } = config

  try {
    const response = await httpClient.upload({
      url: {
        pathname: POST_ATTACHMENT
      },
      data
    })

    return response?.data?.uuid || ''
  } catch (err) {
    catchError(err)

    return false
  }
}

export const deleteAttachment = async (config: any = {}) => {
  const { params = {} } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: DELETE_ATTACHMENT,
        params
      }
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const downloadTaskAttachment =
  (config: any = {}) =>
  async () => {
    const { params = {} } = config

    try {
      const { data, headers } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_DELETE_TASK_ATTACHMENT,
          params
        },
        isDownload: true
      })
      const mappedHeaders: any = {}

      Object.keys(headers).forEach((key: string) => {
        mappedHeaders[key.toLocaleLowerCase()] = headers[key]
      })

      const contentDisposition = get(mappedHeaders, 'content-disposition', '')
      const fileName = contentDisposition
        .slice(contentDisposition.indexOf('filename') + 9)
        .replaceAll('"', '')
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url

      link.setAttribute('download', fileName)

      document.body.appendChild(link)

      link.click()
    } catch (err) {
      catchError(err)
    }
  }

export const deleteTask =
  (config: any = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: GET_DELETE_TASK,
          params
        }
      })

      showSuccessModal({
        content: TASK_DELETE_MESSAGE,
        centered: true
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const saveTaskComment =
  (config: any = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_TASK_COMMENT,
          params
        },
        payload
      })

      showSuccessModal({
        content: TASK_COMMENT_SAVE_MESSAGE,
        centered: true
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const deleteTaskComment =
  (config: any = {}) =>
  async (): Promise<boolean> => {
    const { params = {} } = config

    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: DELETE_TASK_COMMENT,
          params
        }
      })

      showSuccessModal({
        content: TASK_COMMENT_DELETE_MESSAGE,
        centered: true
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const downloadTaskCommentAttachment =
  (config: any = {}) =>
  async () => {
    const { params = {} } = config

    try {
      const { data, headers } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_TASK_COMMENT_ATTACHMENT,
          params
        },
        isDownload: true
      })
      const mappedHeaders: any = {}

      Object.keys(headers).forEach((key: string) => {
        mappedHeaders[key.toLocaleLowerCase()] = headers[key]
      })

      const contentDisposition = get(mappedHeaders, 'content-disposition', '')
      const fileName = contentDisposition
        .slice(contentDisposition.indexOf('filename') + 9)
        .replaceAll('"', '')
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url

      link.setAttribute('download', fileName)

      document.body.appendChild(link)

      link.click()
    } catch (err) {
      catchError(err)
    }
  }

export const fetchPatientListTaskModal =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { query = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENTS,
          query
        }
      })

      dispatch(setPatientListTaskModal(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchTaskHistory =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const data = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_TASK_HISTORY,
          params,
          query
        }
      })

      dispatch(setHistoryItems(data))
    } catch (err) {
      catchError(err)
    }
  }

export const updateTask =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { payload = {}, params = {} } = config

    try {
      const data = await httpClient.request({
        method: 'patch',
        url: {
          pathname: GET_TASK,
          params
        },
        payload
      })

      dispatch(setEditableTask(data))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchTaskComments =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const item = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_TASK_COMMENTS,
          params,
          query
        }
      })

      dispatch(setEditableTaskComments(item))

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const deleteTaskAttachment = async (config: any = {}) => {
  const { params = {} } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: DELETE_TASK_ATTACHMENT,
        params
      }
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const downloadAttachment = async (config = {}) => {
  const { params = {} }: any = config

  try {
    const { data, headers } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_DELETE_ATTACHMENT,
        params
      },
      isDownload: true
    })
    const mappedHeaders: any = {}
    Object.keys(headers).forEach((key) => {
      mappedHeaders[key.toLocaleLowerCase()] = headers[key]
    })
    const contentDisposition = get(mappedHeaders, 'content-disposition', '')
    const fileName = contentDisposition
      .slice(contentDisposition.indexOf('filename') + 9)
      .replaceAll('"', '')
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url

    link.setAttribute('download', fileName)

    document.body.appendChild(link)

    link.click()
  } catch (err) {
    catchError(err)
  }
}
